import { applyMiddleware, combineReducers, createStore } from 'redux'
import createSagaMiddleware from 'redux-saga'
import { composeWithDevTools } from 'redux-devtools-extension'
import dynamicMiddlewares from 'redux-dynamic-middlewares'

import mapViewport from '@bikemap/js/integrations/map/reducers/mapViewport'
import { geolocationReducer } from '@bikemap/js/integrations/geolocation'
import { listReducer } from '@bikemap/js/integrations/list'
import { analyticsMiddleware } from '@bikemap/js/integrations/analytics/actions'

import {
    debuggingReducer, filtersReducer, highlightReducer, legacyLinkReducer,
    mapViewReducer, primaryFiltersReducer, queryOnFiltersChangeReducer,
    routeDebugDataReducer, routesReducer, usersReducer,
} from './reducers'
import { DEFAULT_FILTERS } from './settings'
import searchSaga from './sagas'

/**
 * @typedef {Object} SearchState Global state of the search app
 *
 * @property {import("../../integrations/list/reducer").ListState} results
 * @property {import("./reducers/routesReducer").RoutesState} routes
 * @property {import("./reducers/routeDebugDataReducer").RouteDebugDataState} debugData
 * @property {import("./reducers/usersReducer").UsersState} users
 * @property {import("./reducers/primaryFiltersReducer").PrimarySearchFiltersState} primaryFilters
 * @property {import("./reducers/filtersReducer").SearchFiltersState} filters
 * @property {import("./reducers/highlightReducer").HighlightState} highlight
 * @property {import("../../integrations/geolocation/reducer").GeolocationState} geolocation
 * @property {import("./reducers/legacyLinkReducer").LegacyLinkState} isLegacyLink
 * @property {import("./reducers/queryOnFiltersChangeReducer").QueryOnFiltersChangeState} queryOnFiltersChange
 * @property {import("./reducers/debuggingReducer").DebuggingState} debugging
 * @property {import("./reducers/mapViewReducer").MapViewState} isMapView
 * @property {import("@bikemap/js/integrations/map/reducers/mapViewport").MapViewportState} mapViewport
 */

export default function configureStore() {
    /** @type {(state: SearchState, action: Object) => SearchState} */
    const reducer = combineReducers({
        results: listReducer,
        routes: routesReducer({}),
        debugData: routeDebugDataReducer(null),
        users: usersReducer({}),
        primaryFilters: primaryFiltersReducer(null),
        filters: filtersReducer(DEFAULT_FILTERS),
        highlight: highlightReducer(null),
        geolocation: geolocationReducer,
        isLegacyLink: legacyLinkReducer(false),
        queryOnFiltersChange: queryOnFiltersChangeReducer(true),
        debugging: debuggingReducer(false),
        isMapView: mapViewReducer(false),
        mapViewport,
    })

    // Create store for search app data with saga middleware
    const sagaMiddleware = createSagaMiddleware()
    const store = createStore(
        reducer,
        composeWithDevTools(
            applyMiddleware(
                analyticsMiddleware,
                sagaMiddleware,
                dynamicMiddlewares,
            )
        )
    )
    sagaMiddleware.run(searchSaga)

    return store
}
